/**
 * AddressField — GPS-aware text input for the "Property Address" question.
 *
 * Features:
 *   - "Use my location" button — asks the browser for GPS, reverse-geocodes
 *     via OpenStreetMap Nominatim (free, no API key), and pre-fills the field.
 *   - Address stays fully editable — GPS is just a suggestion.
 *   - Captures lat/lng + accuracy metadata as court-grade evidence that the
 *     certifier was physically at the pool (stored alongside the answer).
 *   - Shows the captured coords + accuracy under the field once set.
 *
 * Design notes:
 *   - Nominatim is used at low volume (1 request per inspection creation) —
 *     within their usage policy. We include a descriptive User-Agent as they
 *     require. If the user's org needs higher volume, swap to Google Places or
 *     Mapbox by changing the geocode() function.
 *   - Geolocation errors are surfaced as friendly toasts, never crash the app.
 */
import { useRef, useState } from "react";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

async function searchAddresses(query) {
  // Photon (komoot) — free, no key, built for autocomplete. Biased to Australia.
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=8&lang=en&lat=-33.87&lon=151.21`;
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`Address search failed (${r.status})`);
  const data = await r.json();
  const seen = new Set();
  return (data.features || [])
    .filter((f) => (f.properties?.country || "") === "Australia")
    .map((f) => {
      const p = f.properties || {};
      const line1 = [p.housenumber, p.street].filter(Boolean).join(" ") || p.name;
      const parts = [line1, p.district || p.suburb, p.city || p.town || p.village, p.state, p.postcode].filter(Boolean);
      return [...new Set(parts)].join(", ");
    })
    .filter((s) => s && !seen.has(s) && seen.add(s))
    .slice(0, 5);
}

async function reverseGeocode(lat, lng) {
  // OpenStreetMap Nominatim — free, no key, ~1 req/sec limit is plenty for us.
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`;
  const r = await fetch(url, {
    headers: {
      "Accept": "application/json",
      // Nominatim requires a descriptive identifier per their usage policy.
      "User-Agent": "PoolAndSpaCertification/1.0",
    },
  });
  if (!r.ok) throw new Error(`Geocode failed (${r.status})`);
  const data = await r.json();
  return data.display_name || "";
}

function requestGeolocation(opts = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported on this device"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000, ...opts },
    );
  });
}

const AddressField = ({ value, onChange, testid, coordsPreview }) => {
  const [detecting, setDetecting] = useState(false);
  const [meta, setMeta] = useState(coordsPreview || null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  const onType = (v) => {
    onChange(v, meta);
    clearTimeout(debounceRef.current);
    if (v.trim().length < 4) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const items = await searchAddresses(v);
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch { /* offline or rate-limited — just no suggestions */ }
    }, 350);
  };

  const pick = (s) => {
    onChange(s, meta);
    setSuggestions([]);
    setOpen(false);
  };

  const useMyLocation = async () => {
    setDetecting(true);
    try {
      const pos = await requestGeolocation();
      const { latitude, longitude, accuracy } = pos.coords;
      const capturedAt = new Date().toISOString();
      let address = "";
      try {
        address = await reverseGeocode(latitude, longitude);
      } catch {
        // Reverse geocode failed — still keep coords, let user type the address.
      }
      const newMeta = {
        latitude, longitude,
        accuracy_m: Math.round(accuracy),
        captured_at: capturedAt,
        source: "gps+nominatim",
      };
      setMeta(newMeta);
      if (address) {
        onChange(address, newMeta);
        toast.success("Address detected — please verify the details");
      } else {
        onChange(value || "", newMeta);
        toast.warning("Location captured but address lookup failed. Please type the address.");
      }
    } catch (err) {
      const msg = err?.code === 1 ? "Location permission denied — please enable location in your browser"
        : err?.code === 2 ? "Location unavailable — check GPS / try again outdoors"
        : err?.code === 3 ? "Location request timed out — try again"
        : err?.message || "Could not get your location";
      toast.error(msg);
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex flex-wrap items-stretch gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <input
            data-testid={testid}
            value={value}
            onChange={(e) => onType(e.target.value)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            placeholder="Start typing the address…"
            autoComplete="off"
            className="h-11 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"
          />
          {open && (
            <ul data-testid={`${testid}-suggestions`}
              className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border-2 border-slate-900 shadow-xl max-h-64 overflow-y-auto">
              {suggestions.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    data-testid={`${testid}-suggestion`}
                    onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-100 flex items-start gap-2"
                  >
                    <MapPin size={14} className="mt-0.5 shrink-0 text-slate-400" />
                    <span>{s}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={useMyLocation}
          disabled={detecting}
          data-testid={`${testid}-use-location`}
          className="h-11 px-3 inline-flex items-center gap-1.5 border-2 border-slate-900 bg-slate-900 text-white font-bold text-xs uppercase tracking-wider hover:bg-slate-800 disabled:opacity-60 whitespace-nowrap"
        >
          {detecting ? <Loader2 size={14} className="animate-spin"/> : <MapPin size={14} strokeWidth={2.5}/>}
          {detecting ? "Locating…" : "Use my location"}
        </button>
      </div>
      {meta && (
        <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5" data-testid={`${testid}-coords`}>
          <CheckCircle2 size={11} className="text-emerald-600" strokeWidth={2.5}/>
          GPS: {meta.latitude.toFixed(6)}, {meta.longitude.toFixed(6)} · ±{meta.accuracy_m}m · captured {new Date(meta.captured_at).toLocaleTimeString()}
          <span className="text-slate-400">— stored as court-grade evidence</span>
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">
        Tip: start typing and pick from the suggestions, or tap <strong>Use my location</strong> to auto-fill from GPS. Always double-check the street number and unit.
      </p>
    </div>
  );
};

export default AddressField;
